<script setup lang="ts">
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
import { formatDateForApi, formatDateToPtBR, parseCivilDate } from '@/shared/lib';
import { Chip, ColoredButton, IconImage } from '@/shared/ui';
import DatePicker from '@/shared/ui/datepicker/DatePicker.vue';
import { Label } from '@/shared/ui/label';
import { SearchCombobox, type SearchComboboxOption } from '@/shared/ui/search-combobox';
import { computed, ref, watch } from 'vue';

  export interface ModelsFilterValue {
    createdAt: string | null;
    categoryIds: number[];
  }

  const props = withDefaults(
    defineProps<{
      categoryOptions?: SearchComboboxOption[];
      initialCreatedAt?: string | null;
      initialCategoryIds?: number[];
    }>(),
    {
      categoryOptions: () => [],
      initialCreatedAt: null,
      initialCategoryIds: () => [],
    }
  );

  const emit = defineEmits<{
    (e: 'clear'): void;
    (e: 'apply', value: ModelsFilterValue): void;
  }>();

  const selectedDate = ref<Date | null>(null);
  const isDateOpen = ref(false);
  const currentCategoryValue = ref('');
  const categorySearchTerm = ref('');
  const selectedCategories = ref<SearchComboboxOption[]>([]);

  watch(
    () => props.initialCreatedAt,
    (createdAt) => {
      selectedDate.value = createdAt ? parseCivilDate(createdAt) : null;
    },
    { immediate: true }
  );

  watch(
    [() => props.categoryOptions, () => props.initialCategoryIds],
    ([options, ids]) => {
      selectedCategories.value = options.filter((option) =>
        ids.includes(Number(option.value))
      );
    },
    { immediate: true }
  );

  const availableCategoryOptions = computed<SearchComboboxOption[]>(() => {
    const term = categorySearchTerm.value.trim().toLowerCase();
    return props.categoryOptions.filter(
      (option) =>
        !selectedCategories.value.some((selected) => selected.value === option.value) &&
        (term === '' || option.label.toLowerCase().includes(term))
    );
  });

  function handleCategorySelect(value: string) {
    if (!value) return;
    const option = props.categoryOptions.find((o) => o.value === value);
    if (option && !selectedCategories.value.some((c) => c.value === value)) {
      selectedCategories.value = [
        ...selectedCategories.value,
        { value: option.value, label: option.label },
      ];
    }
    currentCategoryValue.value = '';
    categorySearchTerm.value = '';
  }

  function removeCategory(value: string) {
    selectedCategories.value = selectedCategories.value.filter((c) => c.value !== value);
  }

  function handleClear() {
    selectedDate.value = null;
    selectedCategories.value = [];
    currentCategoryValue.value = '';
    categorySearchTerm.value = '';
    emit('clear');
  }

  function handleApply() {
    emit('apply', {
      createdAt: selectedDate.value ? formatDateForApi(selectedDate.value) : null,
      categoryIds: selectedCategories.value.map((c) => Number(c.value)),
    });
  }

  defineExpose({
    selectedDate,
    isDateOpen,
    selectedCategories,
    categorySearchTerm,
    availableCategoryOptions,
    handleCategorySelect,
    removeCategory,
    handleClear,
    handleApply,
  });
</script>

<template>
  <div class="flex flex-col gap-3 px-6 pt-3 pb-6">
    <h3 class="text-base font-semibold text-[var(--color-gray-07)]">
      Filtros
    </h3>

    <div class="flex flex-col gap-6 sm:flex-row sm:items-start">
      <div class="flex shrink-0 flex-col gap-1.5">
        <Label class="text-base font-medium text-[var(--color-gray-07)]">Data de Criação</Label>
        <button
          type="button"
          class="flex h-9 w-[182px] items-center gap-2 bg-white-01 px-3 border border-[var(--color-gray-02)] rounded-lg text-[var(--color-gray-03)] focus:outline-none focus:border-[var(--theme-secondary-ink)] font-medium"
          @click="isDateOpen = true"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
            class="h-5 w-5 shrink-0"
          />
          <span class="truncate text-left text-sm">{{
            selectedDate !== null ? formatDateToPtBR(selectedDate) : 'Selecione uma data'
          }}</span>
        </button>
        <DatePicker
          v-model:selected-date="selectedDate"
          v-model:is-open="isDateOpen"
          :default-to-today="false"
          @apply="isDateOpen = false"
          @cancel="isDateOpen = false"
        />
      </div>

      <div class="flex min-w-0 flex-1 flex-col gap-1.5">
        <Label class="text-base font-medium text-[var(--color-gray-07)]">Categorias</Label>
        <SearchCombobox
          v-model="currentCategoryValue"
          :options="availableCategoryOptions"
          placeholder="Buscar"
          search-placeholder="Buscar"
          trigger-icon="search"
          class="rounded-lg border-[var(--color-gray-02)]"
          @update:model-value="handleCategorySelect"
          @search="categorySearchTerm = $event"
        />
        <div
          v-if="selectedCategories.length"
          class="flex flex-wrap gap-1 pt-0.5"
        >
          <Chip
            v-for="cat in selectedCategories"
            :key="cat.value"
            :label="cat.label"
            @remove="removeCategory(cat.value)"
          />
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2.5">
      <ColoredButton
        text="Limpar filtros"
        outlined
        border-color="gray-03"
        color="gray-03"
        class="justify-center"
        @click="handleClear"
      />
      <ColoredButton
        text="Aplicar filtros"
        class="justify-center"
        @click="handleApply"
      />
    </div>
  </div>
</template>
